import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export interface RejectDialogData {
  organizationName: string;
  adminName: string;
}

@Component({
  selector: 'app-reject-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule
  ],
  template: `
    <div class="reject-dialog">
      <h2 mat-dialog-title class="g-just-flex gap-10 align-center">
        <mat-icon color="warn">block</mat-icon>
        Reject Organization
      </h2>
      
      <mat-dialog-content>
        <p class="warning-text">
          <mat-icon class="warning-icon">warning</mat-icon>
          You are about to reject <strong>{{ data.organizationName }}</strong>
        </p>
        
        <p class="info-text">
          This action will:
        </p>
        <ul class="details-list">
          <li>Delete the organization from the system</li>
          <li>Send a rejection notification to {{ data.adminName }}</li>
          <li>Cannot be undone</li>
        </ul>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Reason for Rejection</mat-label>
          <textarea 
            matInput 
            [(ngModel)]="reason"
            placeholder="Please provide a detailed reason for rejection..."
            rows="4"
            required
            #reasonInput="ngModel">
          </textarea>
          <mat-hint>This will be sent to the administrator</mat-hint>
          @if(reasonInput.touched && !reason) {
            <mat-error>Reason is required</mat-error>
          }
        </mat-form-field>
      </mat-dialog-content>
      
      <mat-dialog-actions align="end" class="gap-10">
        <button mat-stroked-button (click)="onCancel()">
          Cancel
        </button>
        <button 
          mat-flat-button 
          color="warn"
          [disabled]="!reason || reason.trim().length === 0"
          (click)="onReject()">
          <mat-icon>block</mat-icon>
          Reject
        </button>
      </mat-dialog-actions>
    </div>
  `,
  styles: [`
    .reject-dialog {
      min-width: 400px;
    }

    mat-dialog-content {
      padding: 20px 0;
      min-height: 200px;
    }

    .warning-text {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px;
      background: #fff3e0;
      border-left: 4px solid #f57c00;
      border-radius: 4px;
      margin-bottom: 15px;
      color: rgba(0, 0, 0, 0.87);
    }

    .warning-icon {
      color: #f57c00;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    .info-text {
      margin: 15px 0 8px 0;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.87);
    }

    .details-list {
      margin: 8px 0 20px 0;
      padding-left: 20px;
      color: rgba(0, 0, 0, 0.6);
    }

    .details-list li {
      margin: 6px 0;
      line-height: 1.5;
    }

    .full-width {
      width: 100%;
    }

    mat-dialog-actions {
      padding: 15px 0 0 0;
      margin: 0;
    }

    .gap-10 {
      gap: 10px;
    }

    .align-center {
      align-items: center;
    }

    .g-just-flex {
      display: flex;
    }

    button mat-icon {
      margin-right: 4px;
      font-size: 18px;
      width: 18px;
      height: 18px;
    }
  `]
})
export class RejectDialogComponent {
  reason: string = '';

  constructor(
    public dialogRef: MatDialogRef<RejectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RejectDialogData
  ) {}

  onReject(): void {
    if (this.reason && this.reason.trim().length > 0) {
      this.dialogRef.close({ reason: this.reason.trim() });
    }
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }
}